import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import SaleFormDialog from "@/components/SaleFormDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { http, formatApiError, formatEuros, formatDateFr } from "@/lib/api";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, LineChart, Line, Legend } from "recharts";
import { Loader2, Plus, Pencil, Trash2, Euro, Trophy, TrendingDown, TrendingUp, FileSignature, CheckCircle2 } from "lucide-react";

export default function AdminSales() {
  const [sales, setSales] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterUser, setFilterUser] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [metric, setMetric] = useState("commission_negociateur_total");

  const load = async () => {
    setLoading(true);
    try {
      const [sl, us, st] = await Promise.all([
        http.get("/sales"),
        http.get("/users"),
        http.get("/sales/stats"),
      ]);
      setSales(sl.data);
      setUsers(us.data);
      setStats(st.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = sales.filter((s) => {
    if (filterUser !== "all" && s.user_id !== filterUser) return false;
    if (filterStatus !== "all" && s.status !== filterStatus) return false;
    return true;
  });

  const metricsConfig = {
    ca_total: { label: "CA total", format: formatEuros, color: "#1D4ED8" },
    commission_agence_total: { label: "Commission agence", format: formatEuros, color: "#F59E0B" },
    commission_negociateur_total: { label: "Commission négociateur", format: formatEuros, color: "#16A34A" },
    nb_ventes: { label: "Nombre de ventes", format: (v) => v, color: "#7C3AED" },
  };

  const ranked = useMemo(() => {
    if (!stats) return [];
    return [...stats.per_user].sort((a, b) => (b[metric] || 0) - (a[metric] || 0));
  }, [stats, metric]);
  const maxMetric = Math.max(1, ...ranked.map((u) => u[metric] || 0));

  const submit = async (payload) => {
    setSaving(true);
    try {
      if (editing) {
        await http.patch(`/sales/${editing.id}`, payload);
        toast.success("Vente mise à jour");
      } else {
        await http.post("/sales", payload);
        toast.success("Vente enregistrée");
      }
      setDialogOpen(false);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!deleting) return;
    try {
      await http.delete(`/sales/${deleting.id}`);
      toast.success("Vente supprimée");
      setDeleting(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading || !stats) {
    return <Layout><div className="flex items-center justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></Layout>;
  }

  return (
    <Layout>
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4" data-testid="admin-sales">
        <div>
          <p className="label-eyebrow mb-2">Ventes & commissions</p>
          <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">Chiffre d'affaires</h1>
          <p className="mt-2 text-slate-600 max-w-2xl">
            Suivez le CA généré et les commissions perçues. Classement en direct pour mettre l'équipe en émulation.
          </p>
        </div>
        <Button size="lg" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-sale-admin-button" className="gap-2">
          <Plus className="h-4 w-4" /> Ajouter une vente
        </Button>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <BigMoney icon={TrendingUp} label="CA total" value={formatEuros(stats.totals.ca_total)} tone="blue" />
        <BigMoney icon={Euro} label="Com. agence" value={formatEuros(stats.totals.commission_agence_total)} tone="amber" />
        <BigMoney icon={Euro} label="Com. négociateurs" value={formatEuros(stats.totals.commission_negociateur_total)} tone="emerald" />
        <BigMoney icon={FileSignature} label="Compromis" value={stats.totals.nb_compromis} tone="slate" />
        <BigMoney icon={CheckCircle2} label="Actes signés" value={stats.totals.nb_actes} tone="slate" />
      </div>

      {/* Ranking */}
      <Card className="p-6 mb-8" data-testid="sales-ranking-card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <p className="label-eyebrow mb-1">Classement</p>
            <h2 className="font-display font-bold text-2xl">Qui rapporte le plus ?</h2>
          </div>
          <Select value={metric} onValueChange={setMetric}>
            <SelectTrigger className="w-[240px]" data-testid="sales-metric-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(metricsConfig).map(([k, m]) => (<SelectItem key={k} value={k}>{m.label}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        {ranked.length === 0 ? (
          <div className="text-center py-10 text-slate-500">Aucune donnée.</div>
        ) : (
          <div className="space-y-3">
            {ranked.map((u, idx) => {
              const val = u[metric] || 0;
              const pct = (val / maxMetric) * 100;
              const display = metricsConfig[metric].format(val);
              return (
                <div key={u.user_id} className="flex items-center gap-4" data-testid={`sales-rank-${u.user_id}`}>
                  <div className="w-10 text-center">
                    {idx === 0 ? (
                      <div className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-amber-100 text-amber-700"><Trophy className="h-4 w-4" /></div>
                    ) : (
                      <div className="text-sm font-semibold text-slate-400">#{idx + 1}</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between mb-1.5">
                      <div className="font-semibold text-sm text-slate-900 truncate flex items-center gap-2">
                        <span className="h-3 w-3 rounded-full ring-2 ring-white shadow-sm" style={{ backgroundColor: u.color }} />
                        {u.name}
                      </div>
                      <div className="font-display font-bold text-sm tabular-nums">{display}</div>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: metricsConfig[metric].color }} />
                    </div>
                  </div>
                  {idx === ranked.length - 1 && ranked.length > 1 && val < maxMetric * 0.5 ? (
                    <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50 gap-1"><TrendingDown className="h-3 w-3" /> À accompagner</Badge>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* CA bar chart */}
      <Card className="p-6 mb-8">
        <div className="mb-4">
          <p className="label-eyebrow mb-1">Comparaison</p>
          <h2 className="font-display font-bold text-2xl">{metricsConfig[metric].label} par négociateur</h2>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={ranked.map((u) => ({ name: u.name.split(" ")[0], value: u[metric] || 0, color: u.color }))}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" tick={{ fill: "#475569", fontSize: 12 }} axisLine={{ stroke: "#CBD5E1" }} tickLine={false} />
              <YAxis tick={{ fill: "#475569", fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(v) => metric.includes("total") || metric === "ca_total" ? new Intl.NumberFormat("fr-FR", { notation: "compact" }).format(v) : v} />
              <Tooltip contentStyle={{ backgroundColor: "white", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 12 }} formatter={(v) => metricsConfig[metric].format(v)} />
              <Bar dataKey="value" name={metricsConfig[metric].label} fill={metricsConfig[metric].color} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Monthly trend */}
      {stats.monthly_series.length > 0 && (
        <Card className="p-6 mb-8" data-testid="monthly-trend-card">
          <div className="mb-4">
            <p className="label-eyebrow mb-1">Tendance mensuelle</p>
            <h2 className="font-display font-bold text-2xl">CA et commissions par mois</h2>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.monthly_series}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fill: "#475569", fontSize: 11 }} axisLine={{ stroke: "#CBD5E1" }} tickLine={false} />
                <YAxis tick={{ fill: "#475569", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => new Intl.NumberFormat("fr-FR", { notation: "compact" }).format(v)} />
                <Tooltip formatter={(v) => formatEuros(v)} contentStyle={{ backgroundColor: "white", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 12 }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="ca" name="CA" stroke="#1D4ED8" strokeWidth={2.5} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="commission_agence" name="Commission agence" stroke="#F59E0B" strokeWidth={2.5} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="commission_negociateur" name="Commission négociateurs" stroke="#16A34A" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Filters + table */}
      <Card className="p-4 mb-4 flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1.5">Négociateur</div>
          <Select value={filterUser} onValueChange={setFilterUser}>
            <SelectTrigger data-testid="sales-filter-user"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              {users.filter((u) => u.role === "collaborator").map((u) => (<SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1.5">Statut</div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="sales-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="compromis">Compromis</SelectItem>
              <SelectItem value="acte">Acte définitif</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-500">Aucune vente pour ces critères.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Date</TableHead>
                <TableHead>Négociateur</TableHead>
                <TableHead>Bien</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Prix</TableHead>
                <TableHead className="text-right">Com. agence</TableHead>
                <TableHead className="text-right">Com. négociateur</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((s) => (
                <TableRow key={s.id} data-testid={`admin-sale-row-${s.id}`}>
                  <TableCell className="whitespace-nowrap text-sm">{formatDateFr(s.signed_date)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: s.user_color }} />
                      <span className="font-medium">{s.user_name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[280px] truncate">{s.property_address || "—"}</TableCell>
                  <TableCell><div className="flex items-center gap-1.5">
                    <Badge variant={s.status === "acte" ? "default" : "secondary"}>{s.status === "acte" ? "Acte" : "Compromis"}</Badge>
                    {s.is_co_sale ? <Badge variant="outline" className="border-primary/40 text-primary bg-primary/5 text-[10px]">Co-vente</Badge> : null}
                  </div></TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{formatEuros(s.sale_price)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatEuros(s.agency_commission)}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold text-emerald-700">{formatEuros(s.negotiator_commission)}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => { setEditing(s); setDialogOpen(true); }} data-testid={`admin-edit-sale-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => setDeleting(s)} data-testid={`admin-delete-sale-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <SaleFormDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditing(null); }}
        onSubmit={submit}
        initial={editing}
        saving={saving}
      />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette vente ?</AlertDialogTitle>
            <AlertDialogDescription>Cette action est définitive.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={doDelete} data-testid="admin-confirm-delete-sale">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

function BigMoney({ icon: Icon, label, value, tone = "blue" }) {
  const tones = {
    blue: "bg-blue-50 text-blue-700 ring-blue-100",
    amber: "bg-amber-50 text-amber-700 ring-amber-100",
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    slate: "bg-slate-100 text-slate-700 ring-slate-200",
  };
  return (
    <Card className="p-5" data-testid={`admin-money-${label}`}>
      <div className="flex items-center gap-3">
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ring-1 ${tones[tone]}`}><Icon className="h-5 w-5" /></div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
          <div className="font-display font-bold text-xl text-slate-900 truncate">{value}</div>
        </div>
      </div>
    </Card>
  );
}
